/**
 * CookieConsentBanner
 * ───────────────────
 * Lightweight GDPR/CCPA cookie consent banner. Shows once until the user
 * makes a choice — choice persisted in localStorage under `vt_cookie_consent`.
 *
 * Categories:
 *   • essential  — always on (required for login, security, etc.)
 *   • analytics  — optional (Google Analytics, etc.)
 *   • marketing  — optional (retargeting, ads)
 *
 * The choice is also broadcast as a window event `vt:cookie-consent` so
 * any analytics/marketing pixel can listen and only fire when allowed.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings as SettingsIcon, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const STORAGE_KEY = 'vt_cookie_consent';
const CONSENT_VERSION = 1;

const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  essential: true,
  analytics: false,
  marketing: false,
  ts: null,
};

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch { return null; }
};

const writeConsent = (consent) => {
  const final = { ...DEFAULT_CONSENT, ...consent, ts: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
  try {
    window.dispatchEvent(new CustomEvent('vt:cookie-consent', { detail: final }));
  } catch { /* noop */ }
  return final;
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState(readConsent);
  useEffect(() => {
    const handler = (e) => setConsent(e.detail);
    window.addEventListener('vt:cookie-consent', handler);
    return () => window.removeEventListener('vt:cookie-consent', handler);
  }, []);
  return consent;
};

const CookieConsentBanner = () => {
  const { isRTL } = useLanguage();
  const t = (ar, en) => (isRTL ? ar : en);

  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    // Defer slightly so the banner doesn't flash on first paint.
    const id = setTimeout(() => {
      if (!readConsent()) setShow(true);
    }, 800);
    return () => clearTimeout(id);
  }, []);

  const acceptAll = () => {
    writeConsent({ analytics: true, marketing: true });
    setShow(false);
  };
  const rejectAll = () => {
    writeConsent({ analytics: false, marketing: false });
    setShow(false);
  };
  const savePrefs = () => {
    writeConsent(prefs);
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="cookie-banner"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 25 }}
        className="fixed inset-x-0 bottom-0 z-[1100] p-3 sm:p-5 pointer-events-none"
        data-testid="cookie-consent-banner"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="pointer-events-auto max-w-5xl mx-auto rounded-2xl border border-[rgba(20,15,10,0.08)] bg-white/95 backdrop-blur shadow-2xl">
          {!expanded ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 sm:p-5">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-flame-50 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-flame-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-ink-900 text-[15px]">
                    {t('نحن نحترم خصوصيتك', 'We value your privacy')}
                  </h3>
                  <p className="text-[13px] text-ink-600 mt-0.5 leading-relaxed">
                    {t(
                      'نستخدم ملفات تعريف الارتباط لتحسين تجربتك، تحليل حركة الموقع، وتخصيص المحتوى. اقرأ',
                      'We use cookies to enhance your experience, analyse site traffic, and personalise content. Read our',
                    )}{' '}
                    <Link to="/legal/privacy" className="text-flame-600 hover:underline font-semibold">
                      {t('سياسة الخصوصية', 'Privacy Policy')}
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto shrink-0">
                <button
                  onClick={() => setExpanded(true)}
                  className="h-10 px-3 rounded-lg border border-[rgba(20,15,10,0.12)] text-ink-700 hover:bg-ink-50 text-[13px] font-semibold inline-flex items-center gap-1.5"
                  data-testid="cookie-customize-btn"
                >
                  <SettingsIcon className="w-3.5 h-3.5" />
                  {t('تخصيص', 'Customize')}
                </button>
                <button
                  onClick={rejectAll}
                  className="h-10 px-4 rounded-lg border border-[rgba(20,15,10,0.12)] text-ink-700 hover:bg-ink-50 text-[13px] font-semibold"
                  data-testid="cookie-reject-btn"
                >
                  {t('رفض الكل', 'Reject all')}
                </button>
                <button
                  onClick={acceptAll}
                  className="h-10 px-5 rounded-lg bg-flame-500 hover:bg-flame-600 text-white text-[13px] font-bold inline-flex items-center gap-1.5"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                  {t('قبول الكل', 'Accept all')}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-bold text-ink-900 text-[16px]">
                    {t('تفضيلات ملفات تعريف الارتباط', 'Cookie preferences')}
                  </h3>
                  <p className="text-[12px] text-ink-500 mt-0.5">
                    {t('اختر فئات الكوكيز التي توافق على استخدامها.', 'Choose the cookie categories you allow.')}
                  </p>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="p-1.5 rounded-lg hover:bg-ink-50"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-ink-500" />
                </button>
              </div>

              <div className="space-y-2.5">
                {/* Essential — always on */}
                <div className="flex items-start justify-between gap-3 p-3 rounded-xl bg-ink-50/60 border border-[rgba(20,15,10,0.06)]">
                  <div>
                    <div className="font-semibold text-ink-900 text-[13px]">
                      {t('الأساسية', 'Essential')} · <span className="text-emerald-600 text-[11px] font-bold uppercase tracking-wide">{t('مطلوبة', 'Required')}</span>
                    </div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5">
                      {t('ضرورية لتسجيل الدخول والأمان وحفظ تفضيلاتك الأساسية.', 'Required for login, security, and saving your basic preferences.')}
                    </div>
                  </div>
                  <input type="checkbox" checked disabled className="mt-1 accent-flame-500 cursor-not-allowed" />
                </div>

                {/* Analytics */}
                <label className="flex items-start justify-between gap-3 p-3 rounded-xl hover:bg-ink-50/60 border border-[rgba(20,15,10,0.06)] cursor-pointer">
                  <div>
                    <div className="font-semibold text-ink-900 text-[13px]">{t('التحليلات', 'Analytics')}</div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5">
                      {t('تساعدنا على فهم كيفية استخدامك للموقع لتحسينه (Google Analytics).', 'Help us understand site usage to improve it (Google Analytics).')}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                    className="mt-1 accent-flame-500"
                    data-testid="cookie-analytics-toggle"
                  />
                </label>

                {/* Marketing */}
                <label className="flex items-start justify-between gap-3 p-3 rounded-xl hover:bg-ink-50/60 border border-[rgba(20,15,10,0.06)] cursor-pointer">
                  <div>
                    <div className="font-semibold text-ink-900 text-[13px]">{t('التسويق', 'Marketing')}</div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5">
                      {t('تتيح لنا عرض إعلانات ذات صلة على منصات أخرى.', 'Let us show relevant ads on other platforms.')}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                    className="mt-1 accent-flame-500"
                    data-testid="cookie-marketing-toggle"
                  />
                </label>
              </div>

              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={rejectAll}
                  className="h-10 px-4 rounded-lg border border-[rgba(20,15,10,0.12)] text-ink-700 hover:bg-ink-50 text-[13px] font-semibold"
                  data-testid="cookie-reject-all-btn"
                >
                  {t('رفض غير الأساسية', 'Reject non-essential')}
                </button>
                <button
                  onClick={savePrefs}
                  className="h-10 px-5 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-[13px] font-bold"
                  data-testid="cookie-save-prefs-btn"
                >
                  {t('حفظ التفضيلات', 'Save preferences')}
                </button>
                <button
                  onClick={acceptAll}
                  className="h-10 px-5 rounded-lg bg-flame-500 hover:bg-flame-600 text-white text-[13px] font-bold"
                  data-testid="cookie-accept-all-btn"
                >
                  {t('قبول الكل', 'Accept all')}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
